import React, { useState } from 'react';
import { CheckCircle, CurrencyDollar, ArrowsClockwise, Bed, Truck, Envelope } from '@phosphor-icons/react';
import { MailInCheckModal } from '../dashboard/MailInCheckModal';

/**
 * EntrySuccess — itemized confirmation + payment trigger after the rider
 * submits an entry form.
 *
 * Receipt math is derived from the FE breakdown (the values that built
 * the on-screen "Submit Entry ($XXX)" button), NOT from the backend
 * `entryResult`. Why: the backend response's `accommodation_fees` only
 * covers the legacy `stalls_requested` numeric count and doesn't see
 * `spot_reservations` (the interactive spot picker rows). Mixing the
 * two produced two different totals on this screen — "Total Due" was
 * undercounting by the spot-picker stalls/RV. We now use the FE values
 * consistently so Total Due === Total Entry Fees === the button total
 * the rider already agreed to.
 */
export const EntrySuccess = ({
  show,
  createdEntries,
  entryResult,
  accommodations,
  totalFees,
  onPayOnline,
  targetFees = 0,
  sponsorTotal = 0,
  spotSelection = { spots: [] },
  accommodationFees = 0,
  stallCost = 0,
  rvCost = 0,
  spotFees = 0,
  processingFee = 0,
  addonFees = 0,
  serviceFee = 0,
  stripeFee = 0,
}) => {
  const [showMailInCheck, setShowMailInCheck] = useState(false);
  // Reservation-only checkouts skip the class-entry section.
  const isAccommodationsOnly = !!entryResult?.accommodations_only || createdEntries.length === 0;

  // Stalls / RV — combine the legacy numeric count + interactive spot-picker
  // rows so the rider sees EVERY line item in one place. Per-day pricing
  // shows nights, weekend pricing just shows the count.
  const interactiveStalls = spotSelection.spots.filter((s) => s.type === 'stall');
  const interactiveRV = spotSelection.spots.filter((s) => s.type === 'rv');
  const legacyStalls = stallCost > 0 ? Math.round(stallCost / (accommodations?.stalls?.price || stallCost) || 1) : 0;
  const legacyRV = rvCost > 0 ? Math.round(rvCost / (accommodations?.rv_hookups?.price || rvCost) || 1) : 0;
  const totalStallCount = legacyStalls + interactiveStalls.length;
  const totalRVCount = legacyRV + interactiveRV.length;

  // Per-spot fee for interactive picker line items — pulled from the
  // selection itself when available, falling back to evenly dividing the
  // spotFees total across the spots.
  const perSpotFee = (s) => {
    if (typeof s.fee === 'number' && s.fee > 0) return s.fee;
    return spotSelection.spots.length > 0 ? spotFees / spotSelection.spots.length : 0;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <div className="max-w-2xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <div className="w-20 h-20 bg-[#34C759]/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle size={40} weight="fill" className="text-[#34C759]" />
          </div>
          <h1 className="text-3xl font-black text-white mb-2" style={{ fontFamily: 'Outfit' }} data-testid="entry-success-title">
            {isAccommodationsOnly ? 'Reservation Confirmed!' : 'Entry Submitted!'}
          </h1>
          <p className="text-[#9E9E9E]">{show.name}</p>
        </div>

        <div className="bg-[#161616] border border-[#262626] rounded-lg p-6 mb-6">
          <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Outfit' }}>
            {isAccommodationsOnly ? 'Your Reservation' : 'Your Entries'}
          </h3>
          <div className="space-y-3">
            {/* Class entries */}
            {createdEntries.map((entry) => (
              <div
                key={entry.id}
                className={`flex items-center justify-between p-3 rounded-lg ${
                  entry.is_rollover ? 'bg-[#FF9500]/5 border border-[#FF9500]/20' : 'bg-[#0A0A0A]'
                }`}
              >
                <div className="flex items-center gap-2">
                  {entry.is_rollover && <ArrowsClockwise size={14} weight="bold" className="text-[#FF9500]" />}
                  <span className="text-white font-semibold text-sm">{entry.class_name}</span>
                </div>
                <span className="text-[#34C759] font-bold" style={{ fontFamily: 'JetBrains Mono' }}>${Number(entry.entry_fee || 0)}</span>
              </div>
            ))}

            {/* Interactive spot-picker stalls — one row per physical spot */}
            {interactiveStalls.map((s, i) => (
              <div key={`istall-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A]" data-testid={`success-stall-spot-${i}`}>
                <span className="text-white font-semibold text-sm flex items-center gap-2">
                  <Bed size={14} className="text-[#34C759]" />
                  Stall — {s.barn || 'Main'} {s.row}-{s.spot_number}
                </span>
                <span className="text-[#34C759] font-bold" style={{ fontFamily: 'JetBrains Mono' }}>${perSpotFee(s).toFixed(2)}</span>
              </div>
            ))}

            {/* Interactive spot-picker RV hookups */}
            {interactiveRV.map((s, i) => (
              <div key={`irv-${i}`} className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A]" data-testid={`success-rv-spot-${i}`}>
                <span className="text-white font-semibold text-sm flex items-center gap-2">
                  <Truck size={14} className="text-[#5E5CE6]" />
                  RV — {s.barn || 'Main'} {s.row}-{s.spot_number}
                </span>
                <span className="text-[#34C759] font-bold" style={{ fontFamily: 'JetBrains Mono' }}>${perSpotFee(s).toFixed(2)}</span>
              </div>
            ))}

            {/* Legacy numeric stalls (no spot picker) — one combined line */}
            {legacyStalls > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A]" data-testid="success-legacy-stalls">
                <span className="text-white font-semibold text-sm flex items-center gap-2">
                  <Bed size={14} className="text-[#34C759]" />
                  Stalls ({legacyStalls})
                </span>
                <span className="text-[#34C759] font-bold" style={{ fontFamily: 'JetBrains Mono' }}>${stallCost.toFixed(2)}</span>
              </div>
            )}

            {/* Legacy numeric RV */}
            {legacyRV > 0 && (
              <div className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A0A]" data-testid="success-legacy-rv">
                <span className="text-white font-semibold text-sm flex items-center gap-2">
                  <Truck size={14} className="text-[#5E5CE6]" />
                  RV Hookups ({legacyRV})
                </span>
                <span className="text-[#34C759] font-bold" style={{ fontFamily: 'JetBrains Mono' }}>${rvCost.toFixed(2)}</span>
              </div>
            )}

            {/* Sub-line items: processing, addons, target race, sponsorship */}
            {processingFee > 0 && (
              <div className="flex items-center justify-between p-2 text-[#9E9E9E] text-sm" data-testid="success-processing-fee">
                <span>Office/Rider Fee</span>
                <span style={{ fontFamily: 'JetBrains Mono' }}>${processingFee.toFixed(2)}</span>
              </div>
            )}
            {addonFees > 0 && (
              <div className="flex items-center justify-between p-2 text-[#9E9E9E] text-sm" data-testid="success-addons">
                <span>Show Merch / Add-ons</span>
                <span style={{ fontFamily: 'JetBrains Mono' }}>${addonFees.toFixed(2)}</span>
              </div>
            )}
            {targetFees > 0 && (
              <div className="flex items-center justify-between p-2 text-[#FF375F] text-sm" data-testid="success-target-fees">
                <span>Target Race</span>
                <span style={{ fontFamily: 'JetBrains Mono' }}>${targetFees.toFixed(2)}</span>
              </div>
            )}
            {sponsorTotal > 0 && (
              <div className="flex items-center justify-between p-2 text-[#FF3B30] text-sm" data-testid="success-sponsor">
                <span>&#9829; Sponsorship</span>
                <span style={{ fontFamily: 'JetBrains Mono' }}>${sponsorTotal.toFixed(2)}</span>
              </div>
            )}
            {serviceFee > 0 && (
              <div className="flex items-center justify-between p-2 text-[#9E9E9E] text-sm">
                <span>Service fee</span>
                <span style={{ fontFamily: 'JetBrains Mono' }} data-testid="success-service-fee">${serviceFee.toFixed(2)}</span>
              </div>
            )}
            {stripeFee > 0 && (
              <div className="flex items-center justify-between p-2 text-[#9E9E9E] text-sm" data-testid="success-stripe-fee">
                <span>C.C. Fee / Payment processing</span>
                <span style={{ fontFamily: 'JetBrains Mono' }}>${stripeFee.toFixed(2)}</span>
              </div>
            )}

            <div className="flex items-center justify-between p-3 bg-[#34C759]/10 border border-[#34C759]/30 rounded-lg">
              <span className="text-white font-bold">Total Due</span>
              <span className="text-[#34C759] font-bold text-lg" style={{ fontFamily: 'JetBrains Mono' }} data-testid="success-grand-total">${totalFees.toFixed(2)}</span>
            </div>
          </div>

          {/* Footer: single Total line that ALWAYS matches Total Due now. */}
          <div className="flex justify-between items-center mt-4 pt-4 border-t border-[#262626]">
            <span className="text-[#9E9E9E] text-sm">Total Entry Fees</span>
            <span className="text-white text-xl font-black" style={{ fontFamily: 'JetBrains Mono' }} data-testid="success-total-entry-fees">${totalFees.toFixed(2)}</span>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={onPayOnline}
            className="w-full py-3.5 bg-[#0096FF] hover:bg-[#00D4FF] text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="pay-online-btn"
          >
            <CurrencyDollar size={20} weight="bold" /> Pay Online Now
          </button>
          <button
            onClick={() => setShowMailInCheck(true)}
            className="w-full py-3 bg-transparent border-2 border-[#FF9500] hover:bg-[#FF9500]/10 text-[#FF9500] font-bold rounded-lg transition-colors flex items-center justify-center gap-2"
            data-testid="pay-by-check-btn"
          >
            <Envelope size={18} weight="bold" /> Pay by Mail-in Check
          </button>
        </div>
      </div>
      {showMailInCheck && (
        <MailInCheckModal
          showId={show.id}
          showName={show.name}
          onClose={() => setShowMailInCheck(false)}
        />
      )}
    </div>
  );
};
